import React from 'react';
import { useFileViewerContext } from 'js/file_viewer/contexts/file_viewer';
import { useLocalErrorForPreviewKey } from 'js/file_viewer/core/atoms/error';
import { currentPreviewKeyAtom, rootFileInfoAtom, rootFileMetadataAtom, rootPreviewKeyAtom, sharedLinkInfoAtom, } from 'js/file_viewer/core/atoms/metadata';
import { useFileViewerAtom, useFileViewerAtomValue } from 'js/file_viewer/core/atoms/use_atom';
import { getPreviewKeyFromFileInfo, isNestedArchiveFilePreviewKey, } from 'js/file_viewer/core/data/preview_key';
import { useNestedArchivePreviewMetadata } from 'js/file_viewer/core/queries/archive_files';
import { FVError, FVErrorCode, ErrorLifecycle } from 'js/file_viewer/core/types/errors';
import { PreviewType } from 'js/file_viewer/core/types/preview_type';
import { DEFAULT_CONFIG } from 'js/file_viewer/core/utils/config';
import { prepareFileInfo, validatePreviewMetadata } from 'js/file_viewer/core/utils/support';
import { makePreviewMetadataRequestPackage } from 'js/react_query_helpers/queries/preview_metadata';
/**
 * Allows us to prefetch preview metadata before the File Viewer is initialized.
 */
export const previewMetadataPrefetch = (files, apiV2ClientShim, queryClient, sharedLinkInfo) => {
    if (!files) {
        return Promise.resolve();
    }
    const preparedFiles = files.map((file) => prepareFileInfo(file, sharedLinkInfo));
    const args = {
        files: preparedFiles,
        include_dimensions: true,
    };
    const requestPackage = makePreviewMetadataRequestPackage(apiV2ClientShim.previewsGetPreviewDataBatch);
    return requestPackage.fetchQuery(queryClient, { apiArg: args, pkgArg: null });
};
/**
 * Fetches preview metadata for a given file ID. Must be run from within a FileViewerContext so we can retrieve
 * the APIv2Client and the root file info.
 */
const usePreviewMetadataQuery = (files, includeDimensions) => {
    const context = useFileViewerContext();
    if (!context) {
        throw new Error('usePreviewMetadataQuery must be called within a FileViewerContext');
    }
    const { apiV2ClientShim } = context;
    const args = {
        files,
        include_dimensions: includeDimensions,
    };
    const requestPackage = React.useMemo(() => makePreviewMetadataRequestPackage(apiV2ClientShim.previewsGetPreviewDataBatch), [apiV2ClientShim.previewsGetPreviewDataBatch]);
    return requestPackage.useQuery({ apiArg: args, pkgArg: null }, {
        enabled: files.some((file) => !!file && (!!file.file_id || !!file.url)),
    });
};
export const useRootPreviewMetadata = () => {
    var _a, _b;
    const [rootFileMetadata, setRootFileMetadata] = useFileViewerAtom(rootFileMetadataAtom);
    const rootPreviewKey = useFileViewerAtomValue(rootPreviewKeyAtom);
    const [rootFileInfo, setRootFileInfo] = useFileViewerAtom(rootFileInfoAtom);
    const sharedLinkInfo = useFileViewerAtomValue(sharedLinkInfoAtom);
    const file = React.useMemo(() => prepareFileInfo(rootFileInfo, sharedLinkInfo), [rootFileInfo, sharedLinkInfo]);
    const fileName = rootFileMetadata === null || rootFileMetadata === void 0 ? void 0 : rootFileMetadata.file_name;
    const fileSize = rootFileMetadata === null || rootFileMetadata === void 0 ? void 0 : rootFileMetadata.size;
    const { data, isLoading } = usePreviewMetadataQuery([file], true);
    const previewFile = (_b = (_a = data === null || data === void 0 ? void 0 : data.apiData) === null || _a === void 0 ? void 0 : _a.results) === null || _b === void 0 ? void 0 : _b[0];
    const localError = useLocalErrorForPreviewKey(rootPreviewKey);
    React.useEffect(() => {
        if (rootFileInfo || !(previewFile === null || previewFile === void 0 ? void 0 : previewFile.file)) {
            return;
        }
        // These cases happen when the File Viewer is initialized with only a shared link URL.
        if (previewFile.file.file_id) {
            setRootFileInfo({
                ...previewFile.file,
                file_id: previewFile.file.file_id,
                previewKey: getPreviewKeyFromFileInfo(previewFile.file),
            });
        }
    }, [previewFile === null || previewFile === void 0 ? void 0 : previewFile.file, rootFileInfo, setRootFileInfo]);
    React.useEffect(() => {
        var _a;
        if (!rootFileMetadata &&
            (previewFile === null || previewFile === void 0 ? void 0 : previewFile.file_size) &&
            (sharedLinkInfo === null || sharedLinkInfo === void 0 ? void 0 : sharedLinkInfo.displayName) &&
            ((_a = previewFile === null || previewFile === void 0 ? void 0 : previewFile.file) === null || _a === void 0 ? void 0 : _a.file_id)) {
            // This happens when the File Viewer is initialized with only a shared link URL.
            setRootFileMetadata({
                file_id: previewFile.file.file_id,
                file_name: sharedLinkInfo.displayName,
                size: previewFile.file_size,
                readOnly: true,
            });
        }
    }, [
        previewFile === null || previewFile === void 0 ? void 0 : previewFile.file.file_id,
        previewFile === null || previewFile === void 0 ? void 0 : previewFile.file_size,
        rootFileMetadata,
        setRootFileMetadata,
        sharedLinkInfo === null || sharedLinkInfo === void 0 ? void 0 : sharedLinkInfo.displayName,
    ]);
    return React.useMemo(() => {
        var _a;
        if (isLoading) {
            return {
                previewType: PreviewType.Loading,
            };
        }
        if (!rootFileInfo && !((_a = previewFile === null || previewFile === void 0 ? void 0 : previewFile.file) === null || _a === void 0 ? void 0 : _a.file_id)) {
            // In some cases, file ID is not provided with the preview data. This can happen for admin-owned files in team
            // folders, for example. If this is the case, and the client didn't provide a file ID, we should show an error.
            return {
                previewType: PreviewType.Error,
                error: new FVError(FVErrorCode.FileNotProvided, ErrorLifecycle.Download),
            };
        }
        const validatedPreviewMetadata = validatePreviewMetadata(previewFile === null || previewFile === void 0 ? void 0 : previewFile.preview, previewFile === null || previewFile === void 0 ? void 0 : previewFile.err, localError, fileName, fileSize, DEFAULT_CONFIG);
        if (validatedPreviewMetadata.error) {
            return {
                previewType: PreviewType.Error,
                error: validatedPreviewMetadata.error,
            };
        }
        return validatedPreviewMetadata;
    }, [
        fileName,
        fileSize,
        isLoading,
        localError,
        previewFile === null || previewFile === void 0 ? void 0 : previewFile.err,
        previewFile === null || previewFile === void 0 ? void 0 : previewFile.file,
        previewFile === null || previewFile === void 0 ? void 0 : previewFile.preview,
        rootFileInfo,
    ]);
};
/**
 * @returns Current preview metadata by aggregating information from local and server state.
 */
export const useCurrentPreviewMetadata = () => {
    const currentPreviewKey = useFileViewerAtomValue(currentPreviewKeyAtom);
    return usePreviewMetadataForPreviewKey(currentPreviewKey);
};
/**
 * @returns Preview metadata for a given preview key.
 */
export const usePreviewMetadataForPreviewKey = (previewKey) => {
    const rootPreviewMetadata = useRootPreviewMetadata();
    const isNestedArchiveFile = previewKey && isNestedArchiveFilePreviewKey(previewKey);
    const nestedArchiveFilePreviewMetadata = useNestedArchivePreviewMetadata(isNestedArchiveFile ? previewKey : undefined);
    if (isNestedArchiveFile && nestedArchiveFilePreviewMetadata) {
        return nestedArchiveFilePreviewMetadata;
    }
    return rootPreviewMetadata;
};
//# sourceMappingURL=preview_metadata.preenvesnextjs.map
